import {
  Checkbox,
  createTheme,
  Drawer,
  Input,
  MantineColorsTuple,
  MultiSelect,
  Notification,
  NumberInput,
  PasswordInput,
  Select,
  Switch,
  Tabs,
  TagsInput,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput, DatePickerInput, TimeInput } from '@mantine/dates';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import colors from 'common/constants/colors.styles';

import InputStyles from '../elements/styles.module.css';

const myColor: MantineColorsTuple = [
  '#ecf5ff',
  '#dee7f2',
  '#bfccdc',
  '#9db0c6',
  '#8198b3',
  '#6e89a8',
  '#6481a4',
  '#536f90',
  '#476383',
  '#375575',
];

const theme = createTheme({
  primaryColor: 'myColor',
  colors: {
    myColor,
  },
  components: {
    Input: Input.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
      },
    }),
    InputWrapper: Input.Wrapper.extend({
      defaultProps: {
        inputWrapperOrder: ['label', 'input', 'description', 'error'],
        size: 'xs',
      },
      styles: {
        label: {
          '--input-label-size': '14px',
          marginTop: 4,
          marginBottom: 4,
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
          fontWeight: 400,
        },
        description: {
          '--input-description-size': '12px',
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
          marginTop: 4,
          marginBottom: 4,
        },
        error: {
          '--input-error-size': '12px',
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
          marginTop: 8,
          marginBottom: 8,
        },
        root: {
          '--input-fz': '16px',
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
        },
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
        hideControls: true,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
        visibilityToggleIcon: ({ reveal }) => {
          if (reveal) {
            return <EyeSlash />;
          }

          return <Eye />;
        },
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    Select: Select.extend({
      defaultProps: {
        searchable: true,
        clearable: true,
        radius: '8px',
        size: 'xs',
        maxDropdownHeight: 200,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        searchable: true,
        clearable: true,
        radius: '8px',
        size: 'xs',
        maxDropdownHeight: 200,
        hidePickedOptions: true,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
        pill: {
          backgroundColor: colors.blue,
          color: colors.white,
        },
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        valueFormat: 'DD/MM/YYYY',
        placeholder: 'DD/MM/YYYY',
        clearable: true,
        radius: '8px',
        size: 'xs',
        rightSection: <></>,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    DateInput: DateInput.extend({
      defaultProps: {
        valueFormat: 'DD/MM/YYYY',
        placeholder: 'DD/MM/YYYY',
        clearable: true,
        radius: '8px',
        size: 'xs',
        rightSection: <></>,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        size: 'xs',
      },
      styles: {
        label: {
          fontSize: '14px',
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
        },
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        size: 'xs',
      },
      styles: {
        label: {
          fontSize: '14px',
          fontFamily:
            'Lato, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
        autosize: true,
        minRows: 4,
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    Drawer: Drawer.extend({
      styles: {
        body: {
          maxHeight: 'calc(100% - 60px)',
          position: 'relative',
          overflow: 'scroll',
        },
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {},
    }),
    Notification: Notification.extend({
      defaultProps: {
        withBorder: true,
      },
    }),
    TagsInput: TagsInput.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        pill: {
          backgroundColor: colors.blue,
          color: colors.white,
        },
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
    TimeInput: TimeInput.extend({
      defaultProps: {
        radius: '8px',
        size: 'xs',
      },
      classNames: {
        input: InputStyles.input,
      },
      styles: {
        input: {
          '--input-bd-focus': colors.blue,
          '--input-disabled-color': colors.black,
          fontSize: 16,
        },
        label: {
          fontSize: '14px',
        },
        error: {
          fontSize: '12px',
        },
      },
    }),
  },
});

export default theme;
